import parseISO from "date-fns/parseISO";
import cloneDeep from "lodash/cloneDeep";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { transformViewDate } from "utils/date-utils";
import {
  IApplicationFieldsState,
  initialState as initialFieldsState
} from "../../StallApplicationFormPage/constants";
import { IReviewFormPopupData } from "./ReviewFormPopup/ReviewFormPopup";

export enum StallApplicationCheckboxType {
  APPROVE = "approve",
  DENY = "deny"
}

export interface ICheckStableEntryStatus {
  isApproved: boolean;
  isDenied: boolean;
  isAwaiting: boolean;
}

export const checkStableEntryStatus = (
  status: racehorse360.StableEntryStatus
): ICheckStableEntryStatus => {
  return {
    isApproved:
      status === racehorse360.StableEntryStatus.STABLE_ENTRY_STATUS_APPROVED,
    isDenied:
      status === racehorse360.StableEntryStatus.STABLE_ENTRY_STATUS_DENIED,
    isAwaiting:
      status === racehorse360.StableEntryStatus.STABLE_ENTRY_STATUS_AWAITING
  };
};

export const getReviewValues = (
  data: racehorse360.IStallApplication
): IApplicationFieldsState => {
  const values: IApplicationFieldsState = cloneDeep(initialFieldsState);
  Object.keys(values).forEach(key => {
    if (key === "arrivalDate" || key === "policyExpirationDate") {
      values[key] = data[key] ? parseISO(data[key]) : null;
    } else {
      values[key] = data[key];
    }
  });
  values.preferencesFirst = data.firstChoiceFacility.id;
  values.preferencesSecond = data.secondChoiceFacility.id;
  values.isStablePlannedToSplit = data.isStablePlannedToSplit.value;

  return values;
};

export const getReviewFormPopupData = (
  stallApplication: racehorse360.IStallApplication,
  stableEntries: racehorse360.IStableEntry[]
): IReviewFormPopupData => ({
  trainerName: stallApplication.contactFullName,
  sleepingRoomsNumber: stallApplication.sleepingRoomsNumber,
  firstChoiceFacility: stallApplication.firstChoiceFacility,
  secondChoiceFacility: stallApplication.secondChoiceFacility,
  arrivalDate: transformViewDate(stallApplication.arrivalDate),
  stableEntries: stableEntries.map(item => ({
    horseName: item.customHorseName || item.horse.name,
    status: item.status
  }))
});

const SET_STABLE_ENTRIES = "SET_STABLE_ENTRIES";
const SET_DISPLAY_PPS = "SET_DISPLAY_PPS";
const SET_DISPLAY_APP = "SET_DISPLAY_APP";
const CHANGE_ENTRY_STATUS = "CHANGE_ENTRY_STATUS";
const DENY_ALL_ENTRIES = "DENY_ALL_ENTRIES";
const APPROVE_ALL_ENTRIES = "APPROVE_ALL_ENTRIES";
const CLEAR_ALL_ENTRIES = "CLEAR_ALL_ENTRIES";

export type TAction =
  | TSetStableEntries
  | TSetDisplayPPs
  | TSetDisplayApp
  | TChangeEntryStatus
  | TApproveAllEntries
  | TDenyAllEntries
  | TClearAllEntries;

interface IStallApplicationRacingSecretaryReviewState {
  shouldDisplayPPs: boolean;
  shouldDisplayApp: boolean;
  stableEntries: racehorse360.IStableEntry[];
}

const initialState: IStallApplicationRacingSecretaryReviewState = {
  shouldDisplayPPs: true,
  shouldDisplayApp: false,
  stableEntries: []
};

export const reducer = (
  state: IStallApplicationRacingSecretaryReviewState = initialState,
  action: TAction
): IStallApplicationRacingSecretaryReviewState => {
  switch (action.type) {
    case SET_STABLE_ENTRIES: {
      return {
        ...state,
        stableEntries: action.stableEntries
      };
    }
    case SET_DISPLAY_PPS: {
      return {
        ...state,
        shouldDisplayPPs: action.shouldDisplayPPs
      };
    }
    case SET_DISPLAY_APP: {
      return {
        ...state,
        shouldDisplayApp: action.shouldDisplayApp
      };
    }
    case CHANGE_ENTRY_STATUS: {
      return {
        ...state,
        stableEntries: state.stableEntries.map(item => {
          if (item.id === action.entryId) {
            return { ...item, status: action.newStatus };
          }

          return item;
        })
      };
    }
    case DENY_ALL_ENTRIES: {
      return {
        ...state,
        stableEntries: state.stableEntries.map(item => {
          return {
            ...item,
            status: racehorse360.StableEntryStatus.STABLE_ENTRY_STATUS_DENIED
          };
        })
      };
    }
    case APPROVE_ALL_ENTRIES: {
      return {
        ...state,
        stableEntries: state.stableEntries.map(item => {
          return {
            ...item,
            status: racehorse360.StableEntryStatus.STABLE_ENTRY_STATUS_APPROVED
          };
        })
      };
    }
    case CLEAR_ALL_ENTRIES: {
      return {
        ...state,
        stableEntries: state.stableEntries.map(item => {
          return {
            ...item,
            status: racehorse360.StableEntryStatus.STABLE_ENTRY_STATUS_AWAITING
          };
        })
      };
    }
    default:
      return state;
  }
};

export const setStableEntries = (stableEntries: racehorse360.IStableEntry[]) =>
  ({
    type: SET_STABLE_ENTRIES,
    stableEntries
  } as const);
type TSetStableEntries = ReturnType<typeof setStableEntries>;

export const setDisplayPPs = (shouldDisplayPPs: boolean) =>
  ({
    type: SET_DISPLAY_PPS,
    shouldDisplayPPs
  } as const);
type TSetDisplayPPs = ReturnType<typeof setDisplayPPs>;

export const setDisplayApp = (shouldDisplayApp: boolean) =>
  ({
    type: SET_DISPLAY_APP,
    shouldDisplayApp
  } as const);
type TSetDisplayApp = ReturnType<typeof setDisplayApp>;

export const changeEntryStatus = (
  entryId: string,
  newStatus: racehorse360.StableEntryStatus
) =>
  ({
    type: CHANGE_ENTRY_STATUS,
    entryId,
    newStatus
  } as const);
type TChangeEntryStatus = ReturnType<typeof changeEntryStatus>;

export const approveAllEntries = () => ({ type: APPROVE_ALL_ENTRIES } as const);
type TApproveAllEntries = ReturnType<typeof approveAllEntries>;

export const denyAllEntries = () => ({ type: DENY_ALL_ENTRIES } as const);
type TDenyAllEntries = ReturnType<typeof denyAllEntries>;

export const clearAllEntries = () => ({ type: CLEAR_ALL_ENTRIES } as const);
type TClearAllEntries = ReturnType<typeof clearAllEntries>;
